import {
  PackageDtoModel,
  PackageResumeModel,
  PackageSplitModel,
  PackageVoModel,
} from '@well/delivery-libs/packages/domain';

function toNum(v: number | null | undefined, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

type Accessors<T> = {
  pieces: (p: T) => number | null | undefined; // DTO: p.pieces, VO: p.count
  width: (p: T) => number | null | undefined;
  height: (p: T) => number | null | undefined;
  length: (p: T) => number | null | undefined;
  weight: (p: T) => number | null | undefined;
  volume?: (p: T) => number | null | undefined; // optional if VO already has volume per piece
};

/**
 * @param dimensionToVolumeFactor conversion from (width*height*length) unit³ to m³
 *  - e.g. if your dims are in mm → set 1e-9
 *  - if in cm → set 1e-6
 *  @param packages array of packages (DTO or VO)
 * @returns calculated package resume
 */
export function calculatePackageResume<T extends PackageVoModel | PackageDtoModel | PackageSplitModel>(
  packages: T[],
  dimensionToVolumeFactor = 1e-9 // adjust if your dims are not in mm
): PackageResumeModel {
  const access: Accessors<T> = {
    pieces: (p) => (p instanceof PackageVoModel ? p.count : p.pieces), // DTO: p.pieces, VO: p.count
    width: (p) => p.width,
    height: (p) => p.height,
    length: (p) => p.length,
    weight: (p) => p.weight,
  };

  let pcsNr = 0;
  let maxDim = 0;
  let sumWeight = 0;
  let sumVolume = 0;

  if (Array.isArray(packages)) {
    for (const item of packages) {
      const pcs = toNum(access.pieces(item));
      const w = toNum(access.width(item));
      const h = toNum(access.height(item));
      const l = toNum(access.length(item));
      const wt = toNum(access.weight(item));

      pcsNr += pcs;
      maxDim = Math.max(maxDim, w, h, l);
      sumWeight += pcs * wt;

      if (access.volume) {
        sumVolume += pcs * toNum(access.volume(item));
      } else {
        sumVolume += pcs * (w * h * l * dimensionToVolumeFactor);
      }
    }
  }

  return new PackageResumeModel(
    '0', // shipmentBaseUuid
    String(maxDim),
    String(sumVolume),
    String(pcsNr),
    String(sumWeight)
  );
}
